import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCoreContext } from "../../../context/global/CoreContext";
import { useRFIContext } from "../../../context/domain/operations/RFIContext";
import {
  updateRFI,
  toggleRFILocked,
  answerAndCloseRFI,
  forwardRFI,
  updateRFIStatus,
  sendRFIToOwner,
  addRFIResponse,
  addRFIChatMessage,
} from "../../../services/api/rfi";
import SendToOwnerModal from "../../../features/operations/rfi/SendToOwnerModal";
import { Button, Chat, ConfirmModal, ToggleSwitch, SignaturePad } from "../../../components/ui";
import { ModalWrapper } from "../../../components/ui";
import { DropDownList } from "../../../components/ui";
import { UserMultiSelect } from "../../../components/ui";
import {
  TicketJobSection,
  useTicketForm,
  useTicketAttachments,
  TicketAttachmentsSection,
} from "../../../features/tickets";
import { getRFIAttachmentFolderPath } from "../../../services/api/rfi";
import {
  RFI_STATUS_COLORS_LIGHT,
  RFI_STATUS_LABELS,
} from "../../../features/operations/rfi/constants";
import {
  formatDate,
  formatDateTime,
  getTodayInPT,
} from "../../../utils/datetime";
import { getUserDisplayName } from "../../../utils/business";

/**
 * RFI Detail Page
 * View and edit RFI details
 *
 * Uses: TicketJobSection from generic ticket system
 * Uses: useRFIContext from createTicketContext factory
 * Uses: RFI API service functions
 */

const RFIDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { allJobsList } = useCoreContext();
  const { getItemById, refreshItems, users } = useRFIContext();

  // Use useTicketForm hook for form state management
  const {
    formData,
    setFormData,
    refs,
    isEdited,
    isLocked: hookIsLocked,
    isLoading: hookIsLoading,
    loadTicket,
    markEdited,
    clearEdited,
    setLocked,
    getRef,
  } = useTicketForm({
    initialState: {
      type: "r&s_form",
      cost_impact: false,
      schedule_impact: false,
      pm_signature: null,
    },
    initialRefs: {
      job: "",
      date_performed: getTodayInPT(),
      subject: "",
      description: "",
      location: "",
      spec_reference: "",
      drawing_reference: "",
      priority: "medium",
      required_by_date: "",
      assigned_to: "",
      scope_of_work: "",
      cost_impact_text: "",
      schedule_impact_text: "",
    },
    onSubmit: async () => {
      // This will be overridden by our custom handleSave
    },
  });

  // RFI metadata (not form fields) - status, response, created_by, etc.
  const [rfiData, setRfiData] = useState(null);
  const [error, setError] = useState(null);

  // Watchers state
  const [watcherIds, setWatcherIds] = useState([]);

  // Use hook's isSubmitting for form operations, keep separate for other operations
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PDF generation state
  const [saveToEgnyte, setSaveToEgnyte] = useState(true);

  // Response state
  const [responseText, setResponseText] = useState("");
  const [isAddingResponse, setIsAddingResponse] = useState(false);
  const [showResponseSection, setShowResponseSection] = useState(false);

  // Send to Owner state
  const [showSendToOwnerModal, setShowSendToOwnerModal] = useState(false);
  const [isSendingToOwner, setIsSendingToOwner] = useState(false);

  // Modal states
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [showAnswerConfirm, setShowAnswerConfirm] = useState(false);
  const [showForwardConfirm, setShowForwardConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Shared ticket attachments (fetch, reorder, upload, ordered filenames for PDF)
  const {
    attachments,
    pendingFiles,
    setPendingFiles,
    isLoadingAttachments,
    isUploadingFiles,
    handleDragEnd,
    getOrderedAttachmentFilenames,
    handleUploadAttachments,
  } = useTicketAttachments({
    ticketId: id,
    ticketIdentifier: rfiData?.rfi_id,
    getAttachmentFolderPathApi: getRFIAttachmentFolderPath,
    uploadAction: "upload_rfi_attachment",
    uploadAdditionalData: rfiData?.rfi_id ? { rfi_id: rfiData.rfi_id } : undefined,
    coverPageLabel:
      rfiData?.rfi_id != null ? `Cover Page - ${rfiData.rfi_id}.pdf` : null,
  });

  useEffect(() => {
    document.title = "RFI Detail - R&S";
  }, []);

  // Load RFI data using useTicketForm's loadTicket
  useEffect(() => {
    /**
     * Loads the RFI by route id into form state, watchers, and metadata.
     *
     * @returns {Promise<void>}
     */
    const loadRFI = async () => {
      if (!id) return;

      try {
        const data = await getItemById(id);
        if (data) {
          // Store full RFI data for metadata (status, response, created_by, etc.)
          setRfiData(data);

          // Load watchers
          setWatcherIds(data.watchers || []);

          // Load form data using hook's loadTicket
          loadTicket(data, {
            locked: data.locked || false,
          });
        }
      } catch (err) {
        console.error("Failed to load RFI:", err);
        setError("Failed to load RFI data");
      }
    };

    loadRFI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getItemById]);

  const currentStatus = rfiData?.status || "new";
  const isLocked = hookIsLocked || rfiData?.locked || false;
  const hasResponse = !!rfiData?.response;
  const isLoading = hookIsLoading;

  /**
   * Uploads pending attachments and surfaces a user-visible error on failure.
   *
   * @returns {Promise<void>}
   */
  const handleUploadAttachmentsWithError = useCallback(async () => {
    try {
      await handleUploadAttachments();
    } catch (err) {
      setError("Failed to upload attachments. Please try again.");
    }
  }, [handleUploadAttachments]);

  /**
   * Merges object updates into form state and marks the RFI edited.
   *
   * @param {Object} updates Partial form fields.
   * @returns {void}
   */
  const handleChange = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    markEdited();
    setError(null);
  }, [setFormData, markEdited]);

  /**
   * Writes a named input into refs (`data-ref`) or form state.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Field change event.
   * @returns {void}
   */
  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked, dataset } = e.target;
      const isRef = dataset?.ref === "true";
      const fieldValue = type === "checkbox" ? checked : value;

      if (isRef) {
        refs.current[name] = fieldValue;
        markEdited();
      } else {
        setFormData((prev) => ({ ...prev, [name]: fieldValue }));
        markEdited();
      }
      setError(null);
    },
    [setFormData, markEdited, refs]
  );

  /**
   * Saves the RFI (including watchers and optional Egnyte PDF) and reloads.
   *
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const payload = { ...formData, ...refs.current };

      // Convert job to integer if present (for API consistency)
      if (payload.job) {
        payload.job = parseInt(payload.job);
      }

      // Add watchers to payload
      payload.watchers = watcherIds;

      // Preserve discussion field (chat messages) - don't overwrite it
      if (rfiData?.discussion) {
        payload.discussion = rfiData.discussion;
      }

      // Convert empty strings to null for optional fields (allows clearing previously set values)
      // For PATCH operations, we need to explicitly send empty values to clear them
      if (!payload.location || payload.location.trim() === "")
        payload.location = null;
      if (!payload.spec_reference || payload.spec_reference.trim() === "")
        payload.spec_reference = null;
      if (!payload.drawing_reference || payload.drawing_reference.trim() === "")
        payload.drawing_reference = null;
      if (!payload.required_by_date) payload.required_by_date = null;
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.scope_of_work || payload.scope_of_work.trim() === "")
        payload.scope_of_work = null;
      if (!payload.cost_impact_text || payload.cost_impact_text.trim() === "")
        payload.cost_impact_text = null;
      if (
        !payload.schedule_impact_text ||
        payload.schedule_impact_text.trim() === ""
      )
        payload.schedule_impact_text = null;

      // Add PDF generation flags if "Save to Egnyte" is checked
      if (saveToEgnyte) {
        payload.generate_pdf = true;
        payload.attachment_filenames = getOrderedAttachmentFilenames();
      }

      await updateRFI(id, payload);
      await refreshItems();

      // Reload the data
      const updatedData = await getItemById(id);
      // Preserve discussion if it's missing from the response
      if (!updatedData.discussion && rfiData?.discussion) {
        updatedData.discussion = rfiData.discussion;
      }
      setRfiData(updatedData);
      // Reload watchers
      setWatcherIds(updatedData.watchers || []);
      // Reload form data using hook
      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      clearEdited();
    } catch (err) {
      console.error("Failed to save RFI:", err);
      setError(err.message || "Failed to save RFI");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Opens the send-to-owner email modal.
   *
   * @returns {void}
   */
  const handleSaveAndSendToOwner = () => {
    setShowSendToOwnerModal(true);
  };

  /**
   * Saves pending edits then emails the RFI to the owner.
   *
   * @param {Object} emailData Owner/CC email payload.
   * @returns {Promise<void>}
   */
  const performSaveAndSendToOwner = async (emailData) => {
    setShowSendToOwnerModal(false);
    setIsSendingToOwner(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }

      // Get ordered attachment filenames for PDF generation
      const attachmentFilenames = getOrderedAttachmentFilenames();

      // Send RFI to owner with email addresses and attachment filenames
      await sendRFIToOwner(id, {
        owner_email: emailData.owner_email,
        cc_emails: emailData.cc_emails || null,
        attachment_filenames: attachmentFilenames,
      });

      // Refresh data
      await refreshItems();
      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);
      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      clearEdited();
    } catch (err) {
      console.error("Failed to save and send RFI to owner:", err);
      setError(err.message || "Failed to save and send RFI to owner");
    } finally {
      setIsSendingToOwner(false);
    }
  };

  /**
   * Saves pending edits (if any) and refreshes the RFI after submit confirm.
   *
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    setShowSubmitConfirm(false);
    setIsSubmitting(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }
      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);
    } catch (err) {
      console.error("Failed to submit RFI:", err);
      setError(err.message || "Failed to submit RFI");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Unlocks the RFI, sets status to open, and preserves the PM signature.
   *
   * @returns {Promise<void>}
   */
  const performToggleLock = async () => {
    setShowUnlockConfirm(false);
    setIsSubmitting(true);
    setError(null);

    try {
      // Capture signature from formData or rfiData before making API calls
      const signatureToPreserve = formData?.pm_signature || rfiData?.pm_signature;

      // Unlock the form
      await toggleRFILocked(id);

      // Set status to "open" (Reviewing) when unlocking
      await updateRFIStatus(id, "open");

      await refreshItems();

      const updatedData = await getItemById(id);

      // Preserve signature if it exists but is missing in updatedData
      if (signatureToPreserve && (!updatedData.pm_signature || updatedData.pm_signature === null || updatedData.pm_signature === "")) {
        updatedData.pm_signature = signatureToPreserve;
      }

      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);

      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });

      // Immediately restore signature in formData after loadTicket
      // This ensures the signature is preserved even if loadTicket overwrote it
      if (signatureToPreserve) {
        setFormData(prev => {
          // Always restore if we have a signature to preserve and current is empty/null
          const currentSignature = prev.pm_signature;
          if (!currentSignature || currentSignature === null || currentSignature === "") {
            return { ...prev, pm_signature: signatureToPreserve };
          }
          return prev;
        });
      }

      setLocked(updatedData.locked || false);
    } catch (err) {
      console.error("Failed to toggle lock:", err);
      setError(err.message || "Failed to toggle lock status");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Requires a response, then opens the answer/close confirm modal.
   *
   * @returns {void}
   */
  const handleAnswerAndClose = () => {
    if (!responseText.trim()) {
      setError("Please enter a response");
      return;
    }
    setShowAnswerConfirm(true);
  };

  /**
   * Adds a PM response without closing or emailing, then unlocks.
   *
   * @returns {Promise<void>}
   */
  const performAnswerOnly = async () => {
    setShowAnswerConfirm(false);
    setIsAddingResponse(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }

      // Add response using add_response endpoint (this locks the RFI)
      await addRFIResponse(id, responseText);

      // Unlock the RFI since we're just adding the answer, not closing
      await toggleRFILocked(id);

      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);

      // Preserve pm_signature from rfiData if it exists but is missing in updatedData
      if (rfiData?.pm_signature && !updatedData.pm_signature) {
        updatedData.pm_signature = rfiData.pm_signature;
      }

      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      setLocked(updatedData.locked || false);
      setResponseText("");
      setShowResponseSection(false);
    } catch (err) {
      console.error("Failed to add answer:", err);
      setError(err.message || "Failed to add answer");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Adds a response, closes the RFI, generates PDF, and emails.
   *
   * @returns {Promise<void>}
   */
  const performAnswerAndClose = async () => {
    setShowAnswerConfirm(false);
    setIsAddingResponse(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }

      // Add response and close the RFI
      await answerAndCloseRFI(id, responseText);

      // Get ordered attachment filenames for PDF generation
      const attachmentFilenames = getOrderedAttachmentFilenames();

      // Update status to closed with attachment filenames (this will generate PDF and send email)
      await updateRFIStatus(id, "closed", attachmentFilenames, true);

      // Lock the form (answer_and_close already locks, but ensure it's locked)
      await toggleRFILocked(id);

      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);

      // Preserve pm_signature from rfiData if it exists but is missing in updatedData
      if (rfiData?.pm_signature && !updatedData.pm_signature) {
        updatedData.pm_signature = rfiData.pm_signature;
      }

      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      setLocked(updatedData.locked || false);
      setResponseText("");
      setShowResponseSection(false);
    } catch (err) {
      console.error("Failed to answer and close RFI:", err);
      setError(err.message || "Failed to answer and close RFI");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Opens the forward-RFI confirm modal.
   *
   * @returns {void}
   */
  const handleForward = () => {
    setShowForwardConfirm(true);
  };

  /**
   * Forwards the RFI after saving pending edits.
   *
   * @returns {Promise<void>}
   */
  const performForward = async () => {
    setShowForwardConfirm(false);
    setIsAddingResponse(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }

      await forwardRFI(id);
      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
    } catch (err) {
      console.error("Failed to forward RFI:", err);
      setError(err.message || "Failed to forward RFI");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Sets RFI status to open (reviewed) after saving pending edits.
   *
   * @returns {Promise<void>}
   */
  const handleMarkAsReviewed = async () => {
    setIsAddingResponse(true);
    setError(null);

    try {
      // Save any pending changes first
      if (isEdited) {
        await handleSave();
      }

      await updateRFIStatus(id, "open");
      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
    } catch (err) {
      console.error("Failed to mark as reviewed:", err);
      setError(err.message || "Failed to mark as reviewed");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Closes the RFI and uploads PDF to Egnyte without emailing.
   *
   * @returns {Promise<void>}
   */
  const performCloseRFI = async () => {
    setShowCloseConfirm(false);
    setIsAddingResponse(true);
    setError(null);

    try {
      // Always save any pending changes (including signature) before closing
      if (isEdited || formData.pm_signature) {
        await handleSave();
      }

      // Get ordered attachment filenames for PDF generation
      const attachmentFilenames = getOrderedAttachmentFilenames();

      // Update status to closed without sending email (but PDF will still be generated and uploaded)
      await updateRFIStatus(id, "closed", attachmentFilenames, false);
      await toggleRFILocked(id);
      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);

      // Preserve pm_signature from rfiData if it exists but is missing in updatedData
      if (rfiData?.pm_signature && !updatedData.pm_signature) {
        updatedData.pm_signature = rfiData.pm_signature;
      }

      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      setLocked(updatedData.locked || false);
    } catch (err) {
      console.error("Failed to close RFI:", err);
      setError(err.message || "Failed to close RFI");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Closes the RFI, generates PDF, and emails the creator.
   *
   * @returns {Promise<void>}
   */
  const performCloseAndEmailRFI = async () => {
    setShowCloseConfirm(false);
    setIsAddingResponse(true);
    setError(null);

    try {
      // Always save any pending changes (including signature) before closing
      if (isEdited || formData.pm_signature) {
        await handleSave();
      }

      // Get ordered attachment filenames for PDF generation
      const attachmentFilenames = getOrderedAttachmentFilenames();

      // Update status to closed with email notification
      await updateRFIStatus(id, "closed", attachmentFilenames, true);
      await toggleRFILocked(id);
      await refreshItems();

      const updatedData = await getItemById(id);
      setRfiData(updatedData);
      setWatcherIds(updatedData.watchers || []);

      // Preserve pm_signature from rfiData if it exists but is missing in updatedData
      if (rfiData?.pm_signature && !updatedData.pm_signature) {
        updatedData.pm_signature = rfiData.pm_signature;
      }

      loadTicket(updatedData, {
        locked: updatedData.locked || false,
      });
      setLocked(updatedData.locked || false);
    } catch (err) {
      console.error("Failed to close and email RFI:", err);
      setError(err.message || "Failed to close and email RFI");
    } finally {
      setIsAddingResponse(false);
    }
  };

  /**
   * Returns to the RFI log.
   *
   * @returns {void}
   */
  const handleCancel = () => {
    navigate("/operations/rfi-log");
  };

  if (isLoading && !rfiData) {
    return <div className="p-4">Loading RFI...</div>;
  }

  if (!rfiData) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
          RFI not found
        </div>
        <Button
          variant="secondary"
          text="Back to RFI Log"
          onClick={handleCancel}
          className="mt-4"
        />
      </div>
    );
  }

  return (
    <div className="p-2 md:p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-blue-900 text-white p-4 rounded-t-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          {/* Column 1: RFI Number and Subtitle */}
          <div>
            <h1 className="text-xl md:text-2xl font-bold">
              {rfiData.rfi_id || "RFI Detail"}
            </h1>
            <p className="text-sm text-blue-200">Request for Information</p>
          </div>

          {/* Column 2: Audit Info */}
          <div className="text-xs text-blue-100 space-y-1 text-center">
            <p>Created by: {rfiData.created_by_name || "Unknown"}</p>
            <p>
              Created:{" "}
              {rfiData.created_at
                ? formatDateTime(rfiData.created_at)
                : "Unknown"}
            </p>
            {rfiData.submitted_at && (
              <p>Submitted: {formatDateTime(rfiData.submitted_at)}</p>
            )}
          </div>

          {/* Column 3: Status and Actions */}
          <div className="flex items-center gap-2 justify-end self-center">
            {/* Status Pill */}
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${RFI_STATUS_COLORS_LIGHT[currentStatus] || "bg-gray-100 text-gray-800"}`}
            >
              {RFI_STATUS_LABELS[currentStatus] || currentStatus}
            </span>

            {/* Status Dropdown Menu */}
            {(() => {
              const menuItems = {};

              // "Mark as reviewed" (open) - only show if not already open
              if (currentStatus !== "open") {
                menuItems["Mark as reviewing"] = handleMarkAsReviewed;
              }

              // "Mark as awaiting response" - only show if not already awaiting_response
              if (currentStatus !== "awaiting_response") {
                menuItems["Mark as awaiting response"] = handleForward;
              }

              // "Mark as closed" - only show if not already closed
              if (currentStatus !== "closed") {
                menuItems["Mark as closed"] = () => setShowCloseConfirm(true);
              }

              // Only show dropdown if there are menu items
              if (Object.keys(menuItems).length > 0) {
                return (
                  <DropDownList
                    listItems={menuItems}
                    buttonIcon="ellipsis"
                    align="right"
                    buttonClassName="text-white"
                  />
                );
              }

              return null;
            })()}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-b-lg shadow p-4 md:p-6">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Job Information */}
          <div className="border-b pb-4">
            <TicketJobSection
              data={{ ...formData, ...refs.current }}
              onChange={(updates) => {
                // Split updates: job and date_performed go to refs, others to state
                const refUpdates = {};
                const stateUpdates = {};

                Object.entries(updates).forEach(([key, value]) => {
                  if (key === "job" || key === "date_performed") {
                    refUpdates[key] = value;
                  } else {
                    stateUpdates[key] = value;
                  }
                });

                // Update refs
                if (Object.keys(refUpdates).length > 0) {
                  Object.assign(refs.current, refUpdates);
                  markEdited();
                }

                // Update state
                if (Object.keys(stateUpdates).length > 0) {
                  handleChange(stateUpdates);
                }
              }}
              readOnly={isLocked}
              jobReadOnly={true}
              dateLabel="Date Performed"
            />

            {/* RFI Number and Project Number - Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RFI Number
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                  {rfiData.rfi_id || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Number
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                  {allJobsList?.find((job) => job.id === parseInt(getRef("job")))
                    ?.project_number || "N/A"}
                </div>
              </div>
            </div>

            {/* Horizontal divider */}
            <div className="border-b border-gray-200 my-4"></div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              {isLocked ? (
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                  {formData.type === "r&s_form"
                    ? "R&S Form"
                    : formData.type === "city_form"
                      ? "City Form"
                      : formData.type || "N/A"}
                </div>
              ) : (
                <select
                  name="type"
                  value={formData.type || "r&s_form"}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="r&s_form">R&S Form</option>
                  <option value="city_form">City Form</option>
                </select>
              )}
            </div>
          </div>

          {/* RFI Details */}
          <div className="border-b pb-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              RFI Details
            </h2>

            {/* Subject */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              {isLocked ? (
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                  {getRef("subject") || ""}
                </div>
              ) : (
                <input
                  key={id}
                  type="text"
                  name="subject"
                  defaultValue={getRef("subject") || ""}
                  onChange={handleInputChange}
                  className="w-full caret-black px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={255}
                  data-ref="true"
                />
              )}
            </div>

            {/* Question */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question <span className="text-red-500">*</span>
              </label>
              {isLocked ? (
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md min-h-[80px] whitespace-pre-wrap">
                  {getRef("description") || ""}
                </div>
              ) : (
                <textarea
                  key={id}
                  name="description"
                  defaultValue={getRef("description") || ""}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  data-ref="true"
                />
              )}
            </div>

            {/* Location, Spec Reference, Drawing Reference - Three column layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {getRef("location") || "-"}
                  </div>
                ) : (
                  <input
                    key={id}
                    type="text"
                    name="location"
                    defaultValue={getRef("location") || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., Grid A-3, Level 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spec Reference
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {getRef("spec_reference") || "-"}
                  </div>
                ) : (
                  <input
                    key={id}
                    type="text"
                    name="spec_reference"
                    defaultValue={getRef("spec_reference") || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., Section 03 30 00 - Cast-in-Place Concrete"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Drawing Reference
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {getRef("drawing_reference") || "-"}
                  </div>
                ) : (
                  <input
                    key={id}
                    type="text"
                    name="drawing_reference"
                    defaultValue={getRef("drawing_reference") || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., S-101, Detail 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Priority and Assignment */}
          <div className="border-b pb-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Priority & Assignment
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md capitalize">
                    {getRef("priority") || "medium"}
                  </div>
                ) : (
                  <select
                    key={id}
                    name="priority"
                    defaultValue={getRef("priority") || "medium"}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Required By Date
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {getRef("required_by_date")
                      ? formatDate(getRef("required_by_date"), "mon DD, YYYY")
                      : "-"}
                  </div>
                ) : (
                  <input
                    key={id}
                    type="date"
                    name="required_by_date"
                    defaultValue={getRef("required_by_date") || ""}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  />
                )}
              </div>
            </div>

            {/* Assign To and Scope of Work - Two column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                    {(() => {
                      const assignedUser = users?.find(u => u.id === parseInt(getRef("assigned_to")));
                      return assignedUser ? getUserDisplayName(assignedUser) : (getRef("assigned_to") || "-");
                    })()}
                  </div>
                ) : (
                  <select
                    key={id}
                    name="assigned_to"
                    defaultValue={getRef("assigned_to") || ""}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  >
                    <option value="">Select team member</option>
                    {users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {getUserDisplayName(user)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scope of Work
                </label>
                {isLocked ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                    {getRef("scope_of_work") || "-"}
                  </div>
                ) : (
                  <input
                    key={id}
                    type="text"
                    name="scope_of_work"
                    defaultValue={getRef("scope_of_work") || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., Foundation & Concrete"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-ref="true"
                  />
                )}
              </div>
            </div>

            {/* Impact Toggles */}
            <div className="space-y-4">
              {/* Cost Impact Toggle */}
              <div>
                <ToggleSwitch
                  text="Potential Cost Impact"
                  isOn={formData.cost_impact}
                  handleToggle={() => {
                    if (isLocked) return;
                    const newCostImpact = !formData.cost_impact;
                    handleChange({
                      cost_impact: newCostImpact,
                    });
                    // Clear ref value when toggled off
                    if (!newCostImpact) {
                      refs.current.cost_impact_text = "";
                    }
                  }}
                  disabled={isLocked}
                />
                {formData.cost_impact && (
                  <div className="mt-2">
                    {isLocked ? (
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md min-h-[60px] whitespace-pre-wrap">
                        {getRef("cost_impact_text") || "-"}
                      </div>
                    ) : (
                      <textarea
                        key={id}
                        name="cost_impact_text"
                        defaultValue={getRef("cost_impact_text") || ""}
                        onChange={handleInputChange}
                        placeholder="Describe the potential cost impact..."
                        rows={2}
                        className="w-full px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        data-ref="true"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Schedule Impact Toggle */}
              <div>
                <ToggleSwitch
                  text="Potential Schedule Impact"
                  isOn={formData.schedule_impact}
                  handleToggle={() => {
                    if (isLocked) return;
                    const newScheduleImpact = !formData.schedule_impact;
                    handleChange({
                      schedule_impact: newScheduleImpact,
                    });
                    // Clear ref value when toggled off
                    if (!newScheduleImpact) {
                      refs.current.schedule_impact_text = "";
                    }
                  }}
                  disabled={isLocked}
                />
                {formData.schedule_impact && (
                  <div className="mt-2">
                    {isLocked ? (
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md min-h-[60px] whitespace-pre-wrap">
                        {getRef("schedule_impact_text") || "-"}
                      </div>
                    ) : (
                      <textarea
                        key={id}
                        name="schedule_impact_text"
                        defaultValue={getRef("schedule_impact_text") || ""}
                        onChange={handleInputChange}
                        placeholder="Describe the potential schedule impact..."
                        rows={2}
                        className="w-full px-3 py-2 caret-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        data-ref="true"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Comments */}
          <Chat
            title="Discussion"
            messages={rfiData?.discussion || []}
            onAddMessage={async (message) => {
              const updatedData = await addRFIChatMessage(id, message);
              // Preserve watchers when updating data
              const preservedWatchers = rfiData?.watchers || [];
              const preservedWatcherIds = watcherIds.length > 0 ? watcherIds : (preservedWatchers.map(w => typeof w === 'object' ? w.id : w));

              setRfiData({
                ...updatedData,
                watchers: updatedData.watchers || preservedWatchers
              });
              // Preserve watcherIds state - use existing if available, otherwise extract from updated data
              if (updatedData.watchers && updatedData.watchers.length > 0) {
                const watcherIdsFromData = (updatedData.watchers || []).map(w => typeof w === 'object' ? w.id : w);
                setWatcherIds(watcherIdsFromData);
              } else if (preservedWatcherIds.length > 0) {
                setWatcherIds(preservedWatcherIds);
              }
            }}
            disabled={isLocked}
          />

          {/* Watchers */}
          <UserMultiSelect
            users={users || []}
            selectedUserIds={watcherIds}
            onChange={(newWatcherIds) => {
              setWatcherIds(newWatcherIds);
              markEdited();
            }}
            title="Watchers"
            helpText="All watchers will be notified via email when an RFI is closed, forwarded or answered."
            disabled={isLocked}
          />

          {/* Official Response Section - Shows when response exists */}
          {hasResponse && (
            <div className="border-b pb-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-700">
                  Official Response
                </h2>
              </div>
              <div className="space-y-3">
                <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-md min-h-[80px] whitespace-pre-wrap">
                  {rfiData.response}
                </div>
                <div className="text-sm text-gray-500">
                  Responded by: {rfiData.response_by_name || "Unknown"} on{" "}
                  {rfiData.response_date
                    ? formatDateTime(rfiData.response_date)
                    : "Unknown date"}. Responses are final and cannot be modified.
                </div>
              </div>
            </div>
          )}

          {/* Provide Response Section - Shows when no response exists */}
          {!hasResponse && (
            <div className="border-b pb-4">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Provide Response
              </h2>
              {showResponseSection ? (
                <div className="space-y-3">
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={4}
                    placeholder="Enter your response to this RFI..."
                    className="w-full caret-black px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="custom"
                      text="Cancel"
                      onClick={() => {
                        setShowResponseSection(false);
                        setResponseText("");
                      }}
                      disabled={isAddingResponse}
                      className="flex-1 bg-gray-300"
                    />
                    <Button
                      variant="secondary"
                      text={isAddingResponse ? "Answering..." : "Answer"}
                      onClick={handleAnswerAndClose}
                      disabled={isAddingResponse || !responseText.trim()}
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button
                    variant="secondary"
                    text="Add Direct Response"
                    onClick={() => setShowResponseSection(true)}
                    className="w-full sm:w-auto"
                  />
                </div>
              )}
            </div>
          )}

          <TicketAttachmentsSection
            ticketIdentifier={rfiData?.rfi_id}
            coverSubtitle={
              formData.type === "r&s_form"
                ? "R&S RFI Form"
                : formData.type === "city_form"
                  ? "City RFI Form"
                  : "RFI Form"
            }
            attachments={attachments}
            handleDragEnd={handleDragEnd}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            pendingFilesHelpText={!rfiData?.rfi_id ? "RFI must be created first before adding attachments." : "Files will be uploaded when the upload button is clicked."}
            isLoadingAttachments={isLoadingAttachments}
            isUploadingFiles={isUploadingFiles}
            handleUploadAttachments={handleUploadAttachmentsWithError}
            disabled={
              isLocked ||
              !rfiData?.rfi_id ||
              isSubmitting ||
              isAddingResponse ||
              isUploadingFiles
            }
            onUploadError={() =>
              setError("Failed to select files. Please try again.")
            }

          />

          {/* Project Manager Signature */}
          <div className="border-b pb-4 mt-6">
            {/* <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Project Manager Signature
            </h2> */}
            <SignaturePad
              value={isLocked ? rfiData?.pm_signature : formData?.pm_signature}
              initialValue={rfiData?.pm_signature}
              onChange={(signature) =>
                handleChange({ pm_signature: signature })
              }
              readOnly={isLocked}
              height={156}
            />
          </div>

        </div>

        {/* PDF Generation Checkbox */}
        {!isLocked && (
          <div className="mt-6 mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
            <input
              type="checkbox"
              id="save-to-egnyte"
              name="saveToEgnyte"
              checked={saveToEgnyte}
              onChange={(e) => setSaveToEgnyte(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="save-to-egnyte"
              className="text-sm font-medium text-gray-700"
            >
              Save to Egnyte
            </label>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          {isLocked ? (
            <Button
              onClick={performToggleLock}
              variant="warningcustom"
              text="Unlock Form"
              className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-gray-900 p-2"
            />
          ) : (
            <>
              <Button
                onClick={handleCancel}
                variant="warningcustom"
                text="Cancel"
                className="flex-1 text-black bg-yellow-100 hover:bg-red-500 hover:text-white py-1.5 px-2"
              />
              <Button
                onClick={handleSave}
                disabled={isSubmitting}
                variant="secondary"
                text={isSubmitting ? "Saving..." : "Save"}
                className="flex-1"
              />
              <Button
                onClick={handleSaveAndSendToOwner}
                disabled={isSubmitting || isSendingToOwner}
                variant="primary"
                text={isSendingToOwner ? "Sending..." : "Save & Send to Owner"}
                className="flex-1"
              />
            </>
          )}
        </div>
      </div>

      {/* Submit Confirmation Modal */}
      <ConfirmModal
        isOpen={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleSubmit}
        title="Submit RFI?"
        text="Once submitted, the RFI will be marked as 'Open' and available for PM review. Are you sure you want to submit?"
        buttonText="Submit RFI"
      />

      {/* Unlock Confirmation Modal */}
      <ConfirmModal
        isOpen={showUnlockConfirm}
        onClose={() => setShowUnlockConfirm(false)}
        onConfirm={performToggleLock}
        title="Unlock RFI?"
        text="This will unlock the RFI for editing. Any response will remain but the RFI can be modified. Are you sure?"
        buttonText="Unlock"
      />

      {/* Answer Confirmation Modal */}
      {showAnswerConfirm && (
        <ModalWrapper
          isOpen={showAnswerConfirm}
          onClose={() => setShowAnswerConfirm(false)}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-center w-full px-8 mr-6">
              Submit Answer?
            </h2>
          </div>
          <div className="text-center">
            <p className="m-4">
              Would you like to just add the answer, or close the RFI and send the answer to the original creator and any current watchers?
            </p>
            <div className="flex flex-row justify-between gap-2 mt-4">
              <Button
                onClick={performAnswerOnly}
                text="Answer, Keep RFI Open"
                variant="secondary"
                className="w-full"
                disabled={isAddingResponse}
              />
              <Button
                onClick={performAnswerAndClose}
                text={isAddingResponse ? "Processing..." : "Answer, Close RFI & Send Email"}
                variant="primary"
                className="w-full"
                disabled={isAddingResponse}
              />
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* Forward Confirmation Modal */}
      <ConfirmModal
        isOpen={showForwardConfirm}
        onClose={() => setShowForwardConfirm(false)}
        onConfirm={performForward}
        title="Forward RFI to Owner?"
        text="This will change the status of the RFI to 'Awaiting Response'. The RFI will need to be forwarded manually, and the RFI will need to be manually closed once a response has been provided. Are you sure you want to proceed?"
        buttonText="Forward"
      />

      {/* Close RFI Confirmation Modal */}
      {showCloseConfirm && (
        <ModalWrapper
          isOpen={showCloseConfirm}
          onClose={() => setShowCloseConfirm(false)}
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-center w-full px-8 mr-6">
              Close RFI?
            </h2>
          </div>
          <div className="text-center">
            <p className="m-4">
              Would you like to close the RFI without sending an email? Or would you like to close the RFI and send an email copy of the RFI to the original creator?
            </p>
            <div className="flex flex-row justify-between gap-2 mt-4">
              <Button
                onClick={performCloseRFI}
                text="Close RFI"
                variant="secondary"
                className="w-full"
                disabled={isAddingResponse}
              />
              <Button
                onClick={performCloseAndEmailRFI}
                text={isAddingResponse ? "Processing..." : "Close and Email"}
                variant="primary"
                className="w-full"
                disabled={isAddingResponse}
              />
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* Send to Owner Modal */}
      <SendToOwnerModal
        isOpen={showSendToOwnerModal}
        onClose={() => setShowSendToOwnerModal(false)}
        onConfirm={performSaveAndSendToOwner}
        isSubmitting={isSendingToOwner}
        rfi={rfiData}
      />
    </div>
  );
};

export default RFIDetail;
